import { PatientContact } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconBuildingSkyscraper } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { IdentityRow } from '../shared/IdentityRow';
import { TeammateAvatar } from '@/components/TeamAvatar';
import { alert } from '@/services/alert';
import { isGoneError } from '@/components/Timeline/types';
import { useTimelineContext } from '../../hooks/useTimelineContext';
import { removeDeletedOrganizationFromPatientContact } from './services';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';

type RelatedContactItemProps = {
  contact: PatientContact;
  /** The insurance policyholder, read-only here (BAR-2967). */
  policyholder?: boolean;
};

function formatTelecomDisplay(telecom: PatientContact['telecom']): string | null {
  if (!telecom || telecom.length === 0) return null;
  const parts = telecom
    .filter((t) => t.value?.trim())
    .map((t) => {
      if (t.system === 'phone') return formatPhoneNumber(t.value!) || t.value;
      return t.value;
    });
  return parts.length > 0 ? parts.join(' • ') : null;
}

/** Returns first phone and first email from telecom for display (phone first, then email) */
function getFirstPhoneAndEmail(telecom: PatientContact['telecom']): PatientContact['telecom'] {
  if (!telecom || telecom.length === 0) return undefined;
  const firstPhone = telecom.find((t) => t.system === 'phone' && t.value?.trim());
  const firstEmail = telecom.find((t) => t.system === 'email' && t.value?.trim());
  const result = [firstPhone, firstEmail].filter(Boolean);
  return result.length > 0 ? (result as PatientContact['telecom']) : undefined;
}

export function RelatedContactItem({ contact, policyholder }: RelatedContactItemProps) {
  const medplum = useMedplum();

  const { patientContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const updatePatient = patientContext?.updatePatient;

  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [organizationTelecom, setOrganizationTelecom] = useState<PatientContact['telecom']>(undefined);

  useEffect(() => {
    if (contact.organization?.reference) {
      medplum
        .readReference(contact.organization)
        .then((org) => {
          if (org.resourceType === 'Organization') {
            setOrganizationName(org.name || 'Partner');
            setOrganizationTelecom(getFirstPhoneAndEmail(org.telecom));
          }
        })
        .catch(async (error) => {
          if (isGoneError(error?.outcome || error)) {
            // Organization was deleted - remove it from Patient.contact
            if (patient && updatePatient && contact.organization?.reference) {
              try {
                await removeDeletedOrganizationFromPatientContact(patient, updatePatient, contact.organization);
              } catch (updateError) {
                // eslint-disable-next-line no-console
                console.error('Failed to remove deleted organization from patient contacts:', updateError);
              }
            }
          } else {
            alert.error('Failed to fetch related partner name');
          }
        });
    }
  }, [contact.organization, medplum, patient, updatePatient]);

  const isOrganization = !!contact.organization;
  const name = isOrganization
    ? organizationName || 'Partner'
    : contact.name?.text ||
      [contact.name?.given?.join(' '), contact.name?.family].filter(Boolean).join(' ') ||
      'Unknown';

  const relationship = contact.relationship?.[0]?.text || contact.relationship?.[0]?.coding?.[0]?.display || 'Contact';
  // A policyholder's first relationship coding is the policyholder role itself, with no display.
  const secondary = policyholder ? 'Insurance policyholder' : relationship;
  const telecomDisplay = formatTelecomDisplay(isOrganization ? organizationTelecom : contact.telecom);

  const leading = isOrganization ? (
    <IconBuildingSkyscraper size={16} style={{ color: 'rgba(0, 0, 0, 0.55)' }} />
  ) : (
    // A patient's contact is not staff: never borrow a teammate's ring by name.
    <TeammateAvatar name={name} resolveRole={false} />
  );

  return <IdentityRow leading={leading} primary={name} secondary={secondary} tertiary={telecomDisplay || undefined} />;
}
